import {
  type CreateReference,
  DeviceUseStatementCode,
  getPeriodDurationDays,
  getReferenceId,
  isValidReference,
} from '@quromedical/fhir-common';
import {
  AdmissionGetResponseSchema,
  type AdmissionGetResponseWithVitals,
  type DeviceAssociationGetResponse,
  DisplayReferenceSchema,
} from '@quromedical/models';
import { compact, first } from '@quromedical/utils';
import { secondsToMilliseconds } from 'date-fns';
import { ActionList, type IconProps, SkeletonProvider } from 'design-system';
import { router } from 'expo-router';
import { useUserSession } from 'hooks';
import { useActiveAssignedAdmissions } from 'hooks/useActiveAssignedAdmissions';
import { useActiveDeviceAssociations } from 'hooks/useDeviceUsage';
import {
  getRelayStatusIssues,
  type RelayStatusIssues,
  type UseRelayStatusResult,
  useRelayStatuses,
} from 'hooks/useRelayStatuses';
import { useLiveData } from 'integration/live-vitals';
import React, { useCallback } from 'react';
import { strings } from 'strings';
import type { z } from 'zod';

const AdmissionWithPatientSchema = AdmissionGetResponseSchema.extend({
  patient: DisplayReferenceSchema,
});
export type AdmissionWithPatient = z.infer<typeof AdmissionWithPatientSchema>;

interface TechReviewListItems {
  relayIssues: RelayStatusIssues;
  patient: CreateReference;
}

interface ListProps {
  isLoading: boolean;
  items: TechReviewListItems[];
  onPatientPress: (id: string) => void;
  header?: boolean;
  critical?: boolean;
}

interface Updated {
  updated: number;
}

const List: React.FC<ListProps> = ({ isLoading, items, onPatientPress }) => {
  const blockColor = 'risk-medium';
  const title = strings.TitleTechnicalReview;

  const mapIssuesToIcons = ({
    lowBattery,
    lowStorage,
    mbs,
    noBluetooth,
    poorNetwork,
  }: RelayStatusIssues): IconProps[] => {
    const color = 'status-warning';
    const iconMap: Record<string, IconProps> = {
      lowBattery: { name: 'battery-2-bar', color },
      lowStorage: { name: 'sd-storage', color },
      mbs: { name: 'patch', color },
      noBluetooth: { name: 'bluetooth-disabled', color },
      poorNetwork: { name: 'signal-cellular-alt-2-bar', color },
    };

    return compact(
      Object.entries({
        lowBattery,
        lowStorage,
        mbs,
        noBluetooth,
        poorNetwork,
      }).map(([key, value]) => (value ? iconMap[key] : undefined)),
    );
  };

  return (
    <SkeletonProvider loading={isLoading}>
      <ActionList.List
        title={title}
        icon="error-outline"
        blockColor={blockColor}
      >
        <ActionList.SubHeading
          subtitle={strings.TechIssueTitle}
          title={strings.TitlePatient}
        />
        {items.map((item) => {
          const { mbs, relay } = item.relayIssues;

          const display = item.patient?.display || strings.DisplayNotFound;

          const patientId = getReferenceId(item.patient);
          const subTitle =
            relay && mbs
              ? 'Relay & patch'
              : relay
                ? 'Relay'
                : mbs
                  ? 'Patch'
                  : '';
          const icons = mapIssuesToIcons(item.relayIssues);

          return (
            <ActionList.Item
              key={patientId}
              id={patientId || ''}
              statusIcons={icons}
              subTitle={subTitle}
              title={display}
              onPress={onPatientPress}
            />
          );
        })}
      </ActionList.List>
    </SkeletonProvider>
  );
};

/**
 * Since there could be many patients' data being updated at the same time, we use a memo to reduce
 * frequent renders
 */
const ListMemo = React.memo<ListProps & Updated>(List, (prev) => {
  const age = Date.now() - prev.updated;
  // using a relatively short memo time since the component isn't very heavy to render
  const stale = age > secondsToMilliseconds(5);
  return !stale;
});

const getActiveRelayIssueForPatient = (
  activeHighIntensityAdmissions: AdmissionWithPatient[],
  relayStatusResult: UseRelayStatusResult,
  deviceAssociations: DeviceAssociationGetResponse[],
) => {
  return activeHighIntensityAdmissions.map((admission) => {
    const patient = isValidReference(admission.patient)
      ? admission.patient
      : undefined;

    if (!patient || relayStatusResult.hasError) {
      return undefined;
    }

    const patientDeviceAssociations = deviceAssociations.filter(
      (association) => {
        const patientId = getReferenceId(patient);
        const associationPatientId = getReferenceId(association.patient);

        if (patientId === associationPatientId) {
          return association;
        }
      },
    );

    // A patient can only have one relay device association
    const patientRelayDeviceAssociation = first(patientDeviceAssociations);

    if (!patientRelayDeviceAssociation) {
      return undefined;
    }

    const relayStatus = relayStatusResult.data.find((statusResult) => {
      if (
        !statusResult.relayId?.value ||
        !patientRelayDeviceAssociation.device?.display
      ) {
        return false;
      }

      return (
        statusResult.relayId?.value ===
        patientRelayDeviceAssociation.device?.display
      );
    });

    if (!relayStatus) {
      return undefined;
    }

    const relayStatusIssues = getRelayStatusIssues(relayStatus);

    if (!relayStatusIssues || !relayStatusIssues.hasAny) {
      return undefined;
    }

    return {
      patient,
      relayIssues: relayStatusIssues,
    };
  });
};

const filterByHighIntensityAdmissions = (
  admissions: AdmissionGetResponseWithVitals[],
): AdmissionWithPatient[] => {
  return admissions.filter((admission) => {
    const days = getPeriodDurationDays(admission.period);
    const isHighIntensity = days ? days <= 7 : false;
    const hasPatient = !!admission.patient;
    const isInProgress = admission.status === 'in-progress';

    return hasPatient && isHighIntensity && isInProgress;
  }) as AdmissionWithPatient[];
};

export const TechReviewList: React.FC = () => {
  const session = useUserSession();

  const locationId = session?.ward;
  const { data: admissions = [], isLoading } = useActiveAssignedAdmissions({
    locationId,
  });
  const { updated } = useLiveData();
  const activeHighIntensityAdmissions =
    filterByHighIntensityAdmissions(admissions);
  const patientIds = compact(
    activeHighIntensityAdmissions.map((admission) =>
      getReferenceId(admission.patient),
    ),
  );

  const deviceAssociations =
    useActiveDeviceAssociations(patientIds).associations;
  const relayDeviceAssociations = deviceAssociations.filter(
    (association) => association.code === DeviceUseStatementCode.relay,
  );
  const deviceIds = compact(
    relayDeviceAssociations.map((association) =>
      getReferenceId(association.device),
    ),
  );
  const relayStatuses = useRelayStatuses(deviceIds);

  const patientRelayIssues = getActiveRelayIssueForPatient(
    activeHighIntensityAdmissions,
    relayStatuses,
    relayDeviceAssociations,
  );
  const activeRelayIssues = compact(patientRelayIssues);

  const onPatientPress = useCallback(
    (id: string) =>
      router.push({
        pathname: `/patient/${id}/profile`,
      }),
    [],
  );

  return (
    <ListMemo
      isLoading={isLoading}
      updated={updated}
      items={activeRelayIssues}
      onPatientPress={onPatientPress}
    />
  );
};
